import React from 'react';
import { Collapse, ListItemButton, Stack, Typography, Drawer } from '@mui/material';
import SubMenu from './SubMenu';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../hooks/useLanguage';
import { useSidebarData } from './hooks/useSidebarData';
import { Link as RLink, useLocation } from 'react-router-dom';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { NavItem } from './types';
import { sendAnalyticsEvent } from '../../../utils/googleAnalytics';

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const drawerWidth = 220;

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const [open, setOpen] = React.useState<boolean[]>([false, true, false]);
  const { t } = useTranslation();
  const SidebarData = useSidebarData();
  const location = useLocation();
  const [lang, handleLangChange] = useLanguage();

  const toggleOpen = (idx: number, sectionName: string) => () => {
    const newOpen = [...open];
    newOpen[idx] = !newOpen[idx];
    // GA4: サイドバー内セクションの開閉を計測
    sendAnalyticsEvent('navigation_menu', {
      action: newOpen[idx] ? 'expand_section' : 'collapse_section',
      section: sectionName,
    });
    setOpen(newOpen);
  };

  const handleMenuItemClick = () => {
    if (onClose) onClose();
  };

  const sidebarContent = (
    <Stack
      style={{
        width: drawerWidth,
        height: '100%',
        padding: '66px 0 12px',
        background: '#007B43',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
      }}
      direction="column"
      spacing={2}
    >
      {/* ...既存メニュー... */}
      <Stack sx={{ width: '100%' }}>
        <ListItemButton onClick={toggleOpen(1, 'real_time_prediction')}>
          <Stack sx={{ width: '100%' }} justifyContent="space-between" direction="row">
            <Typography
              style={{
                color: '#fff',
                fontWeight: 'bold',
                fontSize: '12pt',
              }}
            >
              {t('common.menu.realTimePrediction')}
            </Typography>
            {open[1] ? <ExpandLess sx={{ color: '#fff' }} /> : <ExpandMore sx={{ color: '#fff' }} />}
          </Stack>
        </ListItemButton>
      </Stack>
        <Collapse sx={{ width: '100%' }} in={open[1]} timeout="auto" unmountOnExit>
          {SidebarData.floodPrediction &&
            SidebarData.floodPrediction.map((item: NavItem, index: number) => {
              return (
                <SubMenu
                  selectedPathname={location.pathname}
                  item={{ ...item, title: t(item.title) }}
                  onItemClick={handleMenuItemClick}
                  key={index}
                />
              );
            })}
        </Collapse>

        <Stack sx={{ width: '100%', height: '30px' }}>
          <ListItemButton onClick={toggleOpen(2, 'help')}>
            <Stack sx={{ width: '100%' }} justifyContent="space-between" direction="row">
              <Typography
                style={{
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '12pt',
                }}
                id="commonMenuHelp"
              >
                {t('common.menu.help')}
              </Typography>
              {open[2] ? <ExpandLess sx={{ color: '#fff' }} /> : <ExpandMore sx={{ color: '#fff' }} />}
            </Stack>
          </ListItemButton>
        </Stack>
        <Collapse sx={{ width: '100%' }} in={open[2]} timeout="auto" unmountOnExit>
          {SidebarData.supports &&
            SidebarData.supports.map((item: NavItem, index: number) => {
              return (
                <SubMenu
                  selectedPathname={location.pathname}
                  item={{ ...item, title: t(item.title) }}
                  onItemClick={handleMenuItemClick}
                  key={index}
                />
              );
            })}
        </Collapse>
        <Stack sx={{ width: '100%', height: '30px' }}>
          <ListItemButton onClick={toggleOpen(0, 'climate_risk_assessment')}>
            <Stack sx={{ width: '100%' }} justifyContent="space-between" direction="row">
              <Typography
                style={{
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '12pt',
                }}
              >
                {t('common.menu.climateRiskAssessment')}
              </Typography>
              {open[0] ? <ExpandLess sx={{ color: '#fff' }} /> : <ExpandMore sx={{ color: '#fff' }} />}
            </Stack>
          </ListItemButton>
        </Stack>
        <Collapse sx={{ width: '100%' }} in={open[0]} timeout="auto" unmountOnExit>
          {SidebarData.riskAssessment &&
            SidebarData.riskAssessment.map((item: NavItem, index: number) => {
              return (
                <SubMenu
                  selectedPathname={location.pathname}
                  item={{ ...item, title: t(item.title) }}
                  onItemClick={handleMenuItemClick}
                  key={index}
                />
              );
            })}
        </Collapse>
      <Stack sx={{ width: '100%', mt: 'auto' }}>
        {/* お問い合わせ */}
        <ListItemButton
          component={RLink}
          to="/prediction/contact"
          onClick={() => {
            // GA4: お問い合わせメニューのクリックを計測
            sendAnalyticsEvent('navigation_menu', {
              action: 'click_item',
              menu_item: 'contact',
              link_url: '/prediction/contact',
            });
            handleMenuItemClick();
          }}
          sx={{
            width: '100%',
            height: 48,
            margin: 0,
            color: '#fff',
            padding: '0 20px',
            justifyContent: 'flex-start',
            textDecoration: 'none',
            ...(location.pathname === '/prediction/contact'
              ? { backgroundColor: '#3b627a' }
              : {}),
            '&:hover': {
              backgroundColor: '#3b627a',
            },
          }}
        >
          <Typography style={{ fontSize: 14, fontWeight: 'bold', textAlign: 'left' }}>
            {t('common.menu.info')}
          </Typography>
        </ListItemButton>

        {/* 言語切り替え */}
        <ListItemButton
          onClick={() => {
            // GA4: 言語切替メニューのクリックを計測
            sendAnalyticsEvent('navigation_menu', {
              action: 'click_item',
              menu_item: 'language_switch',
              next_language: lang === 'ja' ? 'en' : 'ja',
            });
            handleLangChange();
          }}
          sx={{
            width: '100%',
            height: 48,
            margin: 0,
            color: '#fff',
            padding: '0 20px',
            justifyContent: 'flex-start',
            '&:hover': {
              backgroundColor: '#3b627a',
            },
          }}
        >
          <Typography style={{ fontSize: 14, fontWeight: 'bold', textAlign: 'left' }}>
            {lang === 'ja' ? 'English' : 'Japanese'}
          </Typography>
        </ListItemButton>
      </Stack>
    </Stack>
  );

  return (
    <Drawer
      anchor="right"
      open={!!isOpen}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        zIndex: 1000,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          zIndex: 1000,
        },
      }}
    >
      {sidebarContent}
    </Drawer>
  );
};

export default Sidebar;
